import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGetArchiveBusinessFile } from '../../../services/loanService';
import { useGetCustomerById } from '../../../services/customerService';
import { ArchiveFolderDocuments, ArchiveHeader, AttentionList, CUSTOMER_FOLDERS, FolderDefinition } from './archiveShared';

// Keep the existing archive folder keys so retained files and upload ownership stay unchanged.
const INDIVIDUAL_FOLDERS: FolderDefinition[] = [
  { key: 'DIRECTORS', title: 'Identity documents', icon: 'fa-id-card-o',
    description: 'The applicant’s RSA ID or passport.', uploadTypes: ['ID_DOCUMENT'] },
  { key: 'COMPANY', title: 'Proof of address', icon: 'fa-home',
    description: 'Address evidence supplied by the individual applicant.', uploadTypes: ['PROOF_OF_ADDRESS'] },
  { key: 'BANKING', title: 'Banking and financial', icon: 'fa-university',
    description: 'Bank confirmation and retained financial records.', uploadTypes: ['BANK_LETTER'] },
  { key: 'REPRESENTATIVE', title: 'Other retained customer records', icon: 'fa-folder-o',
    description: 'Previously supplied records retained on this customer’s file.', uploadTypes: [] }
];

/** A borrower's reusable customer documents, in their four subfolders. */
const ArchiveCustomerDocuments: React.FC = () => {
  const { customerId = '' } = useParams<{ customerId: string }>();
  const { data, isLoading, isError, refetch } = useGetArchiveBusinessFile(customerId, { query: { enabled: !!customerId } });
  const business = data?.business;
  const customer = useGetCustomerById(customerId, { query: { enabled: !!customerId && !!business } });
  const documents = data?.customerDocuments || [];
  const alerts = (data?.attention || []).filter(a => !a.loanReference);

  if (isLoading || (business && !customer.data && !customer.isError)) {
    return <div className="text-center py-5"><div className="spinner-border text-success" role="status"></div></div>;
  }
  if (isError || !business) {
    return <div className="container-fluid py-5 text-center text-muted">This borrower's file could not be loaded. <Link to="/documents">Back to all loan files</Link></div>;
  }
  if (customer.isError || !['INDIVIDUAL', 'CORPORATE'].includes(customer.data?.type || '')) {
    return <div className="container-fluid py-5"><div role="alert" className="alert alert-danger">
      Customer details could not be loaded. Retry before uploading documents.
      {' '}<button type="button" className="btn btn-sm btn-outline-danger" onClick={() => customer.refetch()}>Retry customer details</button>
    </div></div>;
  }
  const isIndividual = customer.data?.type === 'INDIVIDUAL';
  const folders = isIndividual ? INDIVIDUAL_FOLDERS.filter(folder => folder.key !== 'REPRESENTATIVE'
    || documents.some(document => document.folder === folder.key)) : CUSTOMER_FOLDERS;

  return (
    <>
      <ArchiveHeader
        crumbs={[{ label: 'All loan files', to: '/documents' }, { label: business.name || 'Borrower', to: `/documents/files/${customerId}` }, { label: 'Customer documents' }]}
        backTo={`/documents/files/${customerId}`}
        title="Customer documents"
        subtitle={isIndividual
          ? 'Identity, address, banking and financial records for the individual applicant. Current ones can be reused on the next application without being uploaded again.'
          : 'Company, director, representative, banking and financial records. Current ones can be reused on the next application without being uploaded again.'}
      />
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
      <div className="container-fluid">
        {alerts.length > 0 && (
          <div className="alert alert-warning">
            <div className="fw-semibold mb-1">Needs attention</div>
            <AttentionList items={alerts} />
          </div>
        )}
        {folders.map(folder => (
          <ArchiveFolderDocuments
            key={folder.key}
            folder={folder}
            documents={documents.filter(d => d.folder === folder.key)}
            uploadApplicationId={data?.latestApplicationId}
            onUploaded={() => refetch()}
          />
        ))}
      </div>
      </div>
    </>
  );
};

export default ArchiveCustomerDocuments;
